import { useCallback, useEffect, useRef, useState } from 'react';
import { getCandidatePosition, type ICandidatePosition } from '~/components/ConfirmCandidate/utils';
import { IMultipleCounters } from '~/models';
import {
  fireCandidateListCandidateClicked,
  fireConfirmCandidateLeaveClicked,
} from '~/posthogs/events';
import { trackPosthogPageView } from '~/services/posthog/trackingHelpers';

/**
 * Sent with the list's `$pageview`. `/dashboard/job/:jobId` hosts every redesigned
 * shift-details page, and the automatic `$pageview` carries the same path, so the path
 * alone cannot pick out this list in an insight (GOTU-8158, P1).
 */
const CANDIDATE_LIST_PAGE = 'candidate_list';

/** The position reported by the last impression, kept so outcomes can join to it. */
interface IViewedPosition {
  candidateId: string;
  position: ICandidatePosition;
}

interface IUseCandidateImpressionArgs {
  shiftId?: string;
  /** Every counter offer on the shift, in the order the office sees them. */
  counters: IMultipleCounters[];
  /** The counter whose page is open, or `null` while the list is showing. */
  activeCounter: IMultipleCounters | null;
  /** The candidate list itself is on screen: loaded, not empty, no candidate open. */
  isListShowing: boolean;
  /** The current route's pathname, reported as the list `$pageview`'s `path`. */
  pathname: string;
}

/**
 * What the redesigned Confirm Candidates page reports about the office looking at
 * candidates: the list `$pageview`, the row click, and the position every later
 * outcome reports.
 *
 * **The list `$pageview`** fires each time the list comes on screen — on landing and
 * on every return from a candidate. Selecting a candidate is page state, not a route
 * change, so the automatic route-change `$pageview` never sees the list come back. It
 * does not fire when the page opens straight onto a candidate (a deep link, the sole
 * applicant, a decline advancing), and never twice for one showing.
 *
 * **`candidate_list:candidate_clicked`** replaces `confirm_candidate:candidate_viewed`
 * on this page, but covers less: `candidate_viewed` fired on every candidate opened,
 * while the click covers list clicks only. A candidate opened without one — the sole
 * applicant, a deep link, a dashboard or Action Needed card, a decline advancing — now
 * sends no impression, so this page's funnel starts at the click (GOTU-8158, A12). The
 * legacy screen still fires `candidate_viewed`, so the two series stay one per screen.
 *
 * **The position is frozen when a candidate opens**, not recomputed at click time.
 * Declining a candidate invalidates the counter list, so by the time the office
 * confirms the survivor the list has shrunk — recomputing then reported her as
 * "1 of 1" when her own impression, moments earlier, said "2 of 2". That breaks the
 * impression↔outcome join and inverts the question the data exists to answer
 * (GOTU-4816).
 */
export const useCandidateImpression = ({
  shiftId,
  counters,
  activeCounter,
  isListShowing,
  pathname,
}: IUseCandidateImpressionArgs) => {
  const [viewedPosition, setViewedPosition] = useState<IViewedPosition | null>(null);
  const lastViewedCandidateIdRef = useRef<string | null>(null);
  const wasListShowingRef = useRef(false);

  const candidateId = activeCounter?.candidate?.id;
  const offerId = activeCounter?.id;

  // Keyed on the candidate, so a refetch of a shrinking list never moves the frozen
  // position of the candidate already open.
  useEffect(() => {
    if (!candidateId || counters.length === 0) return;
    if (lastViewedCandidateIdRef.current === candidateId) return;

    lastViewedCandidateIdRef.current = candidateId;
    setViewedPosition({ candidateId, position: getCandidatePosition(counters, candidateId) });
  }, [candidateId, counters]);

  // Fires on the hidden → shown edge only. A pathname change while the list stays up
  // re-runs this without firing, because the ref already says it was showing.
  useEffect(() => {
    if (isListShowing && !wasListShowingRef.current) {
      trackPosthogPageView(pathname, { page: CANDIDATE_LIST_PAGE });
    }

    wasListShowingRef.current = isListShowing;
  }, [isListShowing, pathname]);

  /**
   * Reports a row click on the list, with the row's position among every candidate on
   * it. Called before the candidate opens, so it reads the list the office clicked.
   */
  const reportCandidateClicked = useCallback(
    (clickedCandidateId: string) => {
      if (!shiftId || !clickedCandidateId) return;

      const { candidateNumber, candidateCount } = getCandidatePosition(
        counters,
        clickedCandidateId,
      );

      fireCandidateListCandidateClicked({ shiftId, candidateNumber, candidateCount });
    },
    [counters, shiftId],
  );

  /**
   * Fires on "Leave Anyway" from the exit popup (GOTU-5248), before the deferred
   * navigation runs. Falls back to a live derivation when no position has been
   * frozen for this candidate — there is nothing to join to in that case, so a
   * fresh position is the best available.
   */
  const reportLeaveClicked = useCallback(() => {
    if (!shiftId) return;

    const positionAtImpression =
      viewedPosition !== null && viewedPosition.candidateId === candidateId
        ? viewedPosition.position
        : getCandidatePosition(counters, candidateId);

    fireConfirmCandidateLeaveClicked({
      shiftId,
      professionalId: candidateId,
      offerId,
      ...positionAtImpression,
    });
  }, [candidateId, counters, offerId, shiftId, viewedPosition]);

  return { viewedPosition, reportCandidateClicked, reportLeaveClicked };
};
